import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysInvoice } from '../queryKeys';

import { type AdditionalNoteSuccessResponse } from './types';
import { zodSuccessResponseSchema } from './validations';

/**
 * Deletes the additional note for an invoice.
 *
 * @param invoiceId - The ID of the invoice whose additional note should be deleted.
 * @returns The success response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Invoice/operation/delete_invoice_additional_note_invoice__invoice_id__additional_note_delete
 */
export const deleteInvoiceAdditionalNote = async (
  invoiceId: UUID,
): Promise<AdditionalNoteSuccessResponse> => {
  const response = await vestigasApi
    .delete(ENDPOINT.INVOICE.DELETE_ADDITIONAL_NOTE(invoiceId))
    .json<AdditionalNoteSuccessResponse>();

  return zodSuccessResponseSchema.parse(response);
};

/**
 * React Query mutation hook for deleting an additional note from an invoice.
 *
 * @param options - Optional TanStack Query mutation options.
 * @returns A mutation object to trigger the deletion.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Invoice/operation/delete_invoice_additional_note_invoice__invoice_id__additional_note_delete
 */
export const useMutationDeleteInvoiceAdditionalNote = (
  options?: Omit<
    UseMutationOptions<AdditionalNoteSuccessResponse, Error, UUID>,
    'mutationFn' | 'onSettled'
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (invoiceId: UUID) => deleteInvoiceAdditionalNote(invoiceId),
    onSettled: (_data, _error, invoiceId) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeysInvoice.get(invoiceId),
      });
    },
    ...options,
  });
};
